import SplunkOtelWeb from '@splunk/otel-web';
import SplunkSessionRecorder from '@splunk/otel-web-session-recorder';
import { EventName } from '@splunk/otel-web/dist/cjs/upstream/user-interaction/types';
import { Span, trace, Tracer } from '@opentelemetry/api';
import { AxiosError } from 'axios';

export const SPLUNK_FF = 'splunk-rum';
export const SPLUNK_ATTRIBUTES = {
  ACTION_ID: 'action.id',
  ANALYTICS_TAB: 'analytics.tab',
};
export const SPLUNK_SPANS = {
  SEND_DIALER_ACTION_MESSAGE: 'Send Dialer Action Message',
  LOAD_ACTION_COMPOSE: 'Load Action Compose',
  SAVE_ACTION: 'Save Action',
};
export const WEB_APP = 'groove-web-app';
export const OUTLOOK_ADDIN = 'groove-outlook-add-in';
export const GROOVE_EXTENSION = 'groove-extension';

const SPLUNK_REALM = 'us0';
let splunkAppName = 'unknown';

interface ErrorOptions {
  level?: string;
}

export const initializeSplunk = (currentUser, grooveApp: string): void => {
  splunkAppName = grooveApp || 'unknown';
  SplunkOtelWeb.init({
    realm: SPLUNK_REALM,
    rumAccessToken: process.env.SPLUNK_ACCESS_TOKEN,
    applicationName: grooveApp || 'unknown',
    version:
      grooveApp === GROOVE_EXTENSION
        ? chrome.runtime.getManifest().version
        : process.env.REACT_APP_VERSION,
    deploymentEnvironment: process.env.SPLUNK_ENV || process.env.DEPLOY_ENV,
    globalAttributes: {
      'groove.user.id': currentUser?.id,
      'groove.user.role': currentUser?.role,
      'groove.user.email': currentUser?.email,
      'groove.user.name': currentUser?.name,
      'groove.organization.id': currentUser?.organization?.id,
    },
    _experimental_allSpansExtendSession: true,
    _experimental_longtaskNoStartSession: true,
    disableBots: true,
    disableAutomationFrameworks: true,
    debug: false,
    instrumentations: {
      longtask: false,
      errors: true,
      interactions: {
        events: { mousedown: false, mouseup: false },
        shouldPreventSpanCreation: (
          _eventType: EventName,
          element: HTMLElement,
          span: Span,
        ) => {
          span.setAttribute('target_id', element?.id || '');
          span.setAttribute('target_text', element?.textContent || '');
          span.setAttribute('target_class', element?.className || '');
          span.setAttribute('target_role', element?.role || '');
          span.setAttribute(
            'target_action',
            element?.getAttribute('data-splunk-action') ||
              element?.getAttribute('data-dd-action-name') ||
              element?.getAttribute('aria-label') ||
              '',
          );

          return false;
        },
      },
    },
    ignoreUrls: [
      /.*pendo\.io.*/,
      /.*launchdarkly\.com.*/,
      /.*nr-data\.net.*/,
      /.*segment\.io.*/,
      /.*mxpnl\.com.*/,
      /.*mixpanel\.com.*/,
      /.*[chat|play]\.google\.com.*/,
      /^https:\/\/[a-z\-]+\.splunkcloud\.com/ /* eslint no-useless-escape: "off" */,
    ],
  });

  SplunkSessionRecorder.init({
    realm: SPLUNK_REALM,
    rumAccessToken: process.env.SPLUNK_ACCESS_TOKEN,
  });
};

export const wrapWithSplunkSpan = (
  spanName: string,
  invokeFunc: () => void,
  attributes: { [key: string]: string } = {},
): void => {
  const span = trace.getTracer(splunkAppName).startSpan(spanName);
  if (attributes && typeof attributes === 'object')
    Object.keys(attributes).forEach(key =>
      span.setAttribute(key, attributes[key]),
    );
  invokeFunc?.();
  span.end();
};

interface SplunkEventAttributes {
  [key: string]: string;
}

/**
 * Creates a custom Splunk event span with specified attributes and returns the span.
 * @param {string} spanName - Name of the custom event span, used for both span and workflow name.
 * @param {string} routeName - Current route name for specificity, defaulting to "unknown".
 * @param {string} errorMessage - The error message, if applicable.
 * @param {SplunkEventAttributes} attributes - Additional attributes for the event.
 * @returns {Span} - The created span, which can be ended manually.
 */
export const createSplunkCustomEvent = (
  spanName: string,
  routeName = 'unknown',
  errorMessage = '',
  attributes: SplunkEventAttributes = {},
): Span => {
  const tracer: Tracer = trace.getTracer(splunkAppName);
  const span: Span = tracer.startSpan(spanName, {
    attributes: {
      'workflow.name': spanName,
      'route.name': routeName,
      'error.message': errorMessage || 'No error message',
      ...attributes,
    },
  });
  return span; // Returning the span for the caller to manage and end
};

export const storeCurrentUser = (
  currentUser: Record<string, unknown>,
): void => {
  const storeUser = (({ id, name, email, organization, role }) => ({
    id,
    name,
    email,
    organization,
    role,
  }))(currentUser || {});
  window.localStorage.setItem('currentUser', JSON.stringify(storeUser));
};

export const getStoredCurrentUser = (): Record<string, unknown> => {
  let currentUser = {};
  try {
    currentUser = JSON.parse(
      window.localStorage.getItem('currentUser') || '{}',
    );
  } catch (e) {
    console.log('Failed to retreive currentUser for Splunk');
  }
  return currentUser;
};

/**
 * Useful resources:
 *  - [Splunk RUM errors](https://docs.splunk.com/observability/en/gdi/get-data-in/rum/browser/browser-rum-errors.html#splunk-rum-errors)
 */
export const logSplunkError = (
  error: string | Error | AxiosError,
  additionalError?: Error,
  options: ErrorOptions = {},
): void => {
  let errorMessage = 'Unhandled Error';
  let errorDetails: unknown = {};

  if (typeof error === 'string') {
    errorMessage = error;
    if (additionalError) {
      errorDetails = {
        stack: additionalError.stack || 'No stack trace available',
        ...options,
      };
    } else {
      errorDetails = { ...options };
    }
  } else if (
    error instanceof Error ||
    (typeof AxiosError !== 'undefined' && error instanceof AxiosError)
  ) {
    errorMessage = error.message;
    errorDetails = {
      stack: error.stack || 'No stack trace available',
      ...options,
    };
  }

  try {
    SplunkOtelWeb.error(errorMessage, errorDetails);
  } catch (loggingError) {
    console.error('Failed to log error to Splunk:', loggingError);
    console.error('Original error:', errorMessage, errorDetails);
  }
};
